import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";

import type { PageNode } from "@tento-ui/brand/page-model";
import { setTextOrHide, stringProp, stringsProp } from "./node-props";
import { tshVars } from "./tokens";
import type { InstallPlatform } from "./tsh-platform";
import { platformsProp, platformStore } from "./tsh-platform";
import { TshPlatformBar } from "./tsh-platform-bar";

// The hero: a headline, the install control, then the short product preamble a
// reader uses to decide what they just installed. The hero-media slot is the
// seam between the install URL and that preamble: evidence before explanation.
//
// The install line is a real `<button>` wrapping real text rather than a
// styled div, because its whole job is to be copied. The synopsis follows it:
// the action stays in the first scan, while the longer explanation gets the
// quieter editorial rhythm of the fx preamble this page takes as a reference.
//
// The platform bar sits directly on top of that button, and the command below
// it is whatever the bar has selected. Above the button rather than inside it
// because the tabs change what the button SAYS: a control that rewrites the
// line under it has to be read first, and a reader who copies before noticing
// the tabs has copied the wrong shell's syntax.
@component({ name: "tsh-hero" })
export class TshHero extends LonaWebComponent {
  private $eyebrow: HTMLElement = this.$("eyebrow");
  private $headline: HTMLElement = this.$("headline");
  private $synopsis: HTMLElement = this.$("synopsis");
  private $command: HTMLElement = this.$("command");
  private $prompt: HTMLElement = this.$("prompt");
  private $commandText: HTMLElement = this.$("command-text");
  private $copy: HTMLElement = this.$("copy");
  private $meta: HTMLElement = this.$("meta");
  private $platforms: TshPlatformBar = TshPlatformBar.make();
  private platforms: InstallPlatform[] = [];
  private fallbackCommand = "";
  private command = "";
  private unsubscribe: Option<() => void> = null;

  constructor() {
    super();
    // Mounted from here rather than authored into `$html`: `make()` returns an
    // element already upgraded against the registered class, so the field is a
    // `TshPlatformBar` and not an `HTMLElement` waiting for an upgrade that a
    // detached tree never triggers.
    this.$("frame").insertBefore(this.$platforms, this.$command);

    GESTURE_MANAGER.addPointerEvent(this.$command, {
      onClick: () => {
        void navigator.clipboard?.writeText(this.command);
        this.$copy.textContent = "copied";
        window.setTimeout(() => (this.$copy.textContent = "copy"), 1600);
      },
    });
  }

  bind(node: PageNode): void {
    setTextOrHide(this.$eyebrow, stringProp(node, "eyebrow"));
    this.$headline.textContent = stringProp(node, "headline") ?? "";
    const synopsis = stringsProp(node, "synopsis");
    const legacySubheadline = stringProp(node, "subheadline");
    const paragraphs = synopsis.length > 0
      ? synopsis
      : legacySubheadline
      ? [legacySubheadline]
      : [];
    DomUtils.clearChildren(this.$synopsis);
    for (const paragraph of paragraphs) {
      const $p = document.createElement("p");
      $p.textContent = paragraph;
      this.$synopsis.appendChild($p);
    }
    this.$synopsis.toggleAttribute("hidden", paragraphs.length === 0);
    // The flat `command` stays the fallback rather than being replaced by the
    // table: it is what a node carries when it predates the platform bar, and
    // what a server-rendered copy of this page would print with no script.
    this.fallbackCommand = stringProp(node, "command") ?? "";
    this.platforms = platformsProp(node);
    this.$platforms.bind(this.platforms);
    this.renderCommand();
    setTextOrHide(this.$meta, stringProp(node, "meta"));
  }

  connectedCallback(): void {
    super.connectedCallback();
    // Both bars on the page publish into the same store, so this repaints when
    // the CTA's tabs are the ones clicked.
    this.unsubscribe ??= platformStore.subscribe(() => this.renderCommand());
    this.renderCommand();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  /**
   * Draw the selected platform's line — prompt included.
   *
   * The prompt is part of the platform, not decoration: `$` in front of a
   * PowerShell one-liner is a claim about which shell it runs in, and it is
   * wrong. Only the command is copied; the prompt is a `<span>` beside it, so
   * the clipboard never carries it.
   */
  private renderCommand(): void {
    const selected = this.platforms.find((p) => p.id === platformStore.get());
    this.command = selected?.command ?? this.fallbackCommand;
    this.$prompt.textContent = selected?.prompt ?? "$";
    this.$commandText.textContent = this.command;
    this.$command.toggleAttribute("hidden", this.command === "");
  }

  static $styles = [
    tshVars,
    css`
      :host {
        display: block;
        padding: 56px var(--tsh-gutter, 32px) 0;
        font-family: var(--tsh-mono);
        color: var(--tsh-muted, #525252);
      }

      #frame {
        max-width: var(--tsh-wide-panel, 1100px);
      }

      #eyebrow,
      h1,
      tsh-platform-bar,
      #command,
      #meta {
        max-width: var(--tsh-panel, 680px);
      }

      /* The eyebrow is the only role at the smaller size, and it is faint
         rather than coloured — there is no accent on this page to reach for. */
      #eyebrow {
        --font-size: var(--small-size);
        --line-height-ratio: 1.429;
        --margin-top: 0px;
        --margin-bottom: 13.2px; /* 8 box + 5.21 trim */
        color: var(--tsh-faint, #9e9e9e);
      }

      h1 {
        --font-size: calc(var(--h1-size) + 1px);
        --line-height-ratio: 1.429;
        --margin-top: 0px;
        --margin-bottom: 22.7px; /* 12 box + 5.21 + 5.50 trims */
        font-weight: 700;
        color: var(--tsh-fg, #171717);
      }

      #synopsis {
        max-width: var(--tsh-measure, 560px);
      }

      slot[name="media"] {
        display: block;
      }

      #synopsis p {
        --font-size: var(--p-size);
        --line-height-ratio: 1.75;
        --margin-top: 0px;
        /* 35px of visible space: 35 + 7.5px trim on each paragraph. */
        --margin-bottom: 50px;
      }

      /* 28px from the install control to the first line of the preamble: the
         authored value includes the paragraph's 7.5px cap-height trim. */
      #synopsis p:first-child {
        --margin-top: 35.5px;
      }

      #synopsis p:last-child {
        --margin-bottom: 0px;
      }

      /* The bar owns the gap down to the command it rewrites — 4px, tighter
         than any gap between sections, because the two are one control. */
      tsh-platform-bar {
        display: block;
        margin-bottom: 4px;
      }

      /* A button, because it copies. Styled as a line of the page rather than
         as a control: hairline, 10px corner, no fill until hover. */
      #command {
        display: flex;
        align-items: baseline;
        gap: 12px;
        width: 100%;
        max-width: var(--tsh-panel, 680px);
        padding: 14px 16px;
        border: 1px solid var(--tsh-border, #e5e5e5);
        border-radius: var(--tsh-radius, 10px);
        background: transparent;
        font: inherit;
        font-size: calc(var(--p-size) / 0.7);
        text-align: left;
        cursor: pointer;
        color: var(--tsh-fg, #171717);
      }

      #command:hover {
        background: var(--tsh-surface, #fafafa);
      }

      #prompt {
        color: var(--tsh-faint, #9e9e9e);
      }

      #command-text {
        flex: 1;
        min-width: 0;
        overflow-x: auto;
        white-space: pre;
      }

      #copy {
        color: var(--tsh-faint, #9e9e9e);
        font-size: calc(var(--small-size) / 0.7);
        flex-shrink: 0;
      }

      #meta {
        --font-size: var(--small-size);
        --line-height-ratio: 1.429;
        --margin-top: 18.2px; /* 13 box + 5.21 trim */
        --margin-bottom: 0px;
        color: var(--tsh-faint, #9e9e9e);
      }

      [hidden] {
        display: none !important;
      }
    `,
  ];

  static $html = html`
    <div id="frame">
      <p id="eyebrow"></p>
      <h1 id="headline"></h1>
      <button id="command" type="button">
        <span id="prompt">$</span>
        <span id="command-text"></span>
        <span id="copy">copy</span>
      </button>
      <slot name="media"></slot>
      <div id="synopsis"></div>
      <p id="meta"></p>
    </div>
  `;
}
